/**
 * ProviderCard Component
 * 
 * Displays a tenant-owned LLM provider with status and actions.
 */

import { useState } from 'react';
import { RefreshCw, Trash2, Edit, CheckCircle, XCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useI18n } from '@/i18n';
import type { TenantProvider } from '../types';
import { PROVIDER_METADATA } from '../types';
import { ProviderIcon } from './ProviderIcons';

interface ProviderCardProps {
  provider: TenantProvider;
  onValidate: (id: string) => void;
  onEdit: (provider: TenantProvider) => void;
  onDelete: (id: string) => void;
  isValidating?: boolean;
}

export function ProviderCard({
  provider,
  onValidate,
  onEdit,
  onDelete,
  isValidating = false,
}: ProviderCardProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { t } = useI18n();
  
  const metadata = PROVIDER_METADATA[provider.provider_type] || {
    displayName: provider.provider_type,
    icon: '🤖',
    iconBg: 'bg-gray-100 dark:bg-gray-700',
  };

  const formatRelativeTime = (dateString: string | null) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return t('common.timeAgo.justNow');
    if (minutes < 60) return t('common.timeAgo.minutesAgo', { count: minutes });
    if (hours < 24) return t('common.timeAgo.hoursAgo', { count: hours });
    return t('common.timeAgo.daysAgo', { count: days });
  };

  return (
    <Card className={cn(
      'transition-all',
      !provider.is_active && 'opacity-60'
    )}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            {/* Icon */}
            <div className={cn(
              'w-10 h-10 rounded-lg flex items-center justify-center text-xl',
              metadata.iconBg
            )}>
              <ProviderIcon
                providerType={provider.provider_type}
                size={22}
                className="w-[22px] h-[22px] object-contain"
              />
            </div>
            
            {/* Title and type */}
            <div>
              <div className="flex items-center gap-2">
                <CardTitle className="text-base">{provider.name}</CardTitle>
                
                {/* Status badges */}
                {!provider.is_active && (
                  <Badge variant="secondary" className="text-xs">
                    {t('admin.llmConfig.validation.inactive')}
                  </Badge>
                )}
                
                {provider.is_validated ? (
                  <Badge variant="default" className="text-xs bg-green-600 hover:bg-green-600">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    {t('admin.llmConfig.validation.validated')}
                  </Badge>
                ) : provider.validation_error ? (
                  <Badge variant="destructive" className="text-xs">
                    <XCircle className="w-3 h-3 mr-1" />
                    {t('admin.llmConfig.validation.failed')}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-xs">
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    {t('admin.llmConfig.validation.notValidated')}
                  </Badge>
                )}
              </div>
              
              <CardDescription className="mt-1">
                {metadata.displayName}
              </CardDescription>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pb-3">
        {/* Endpoint (if applicable) */}
        {provider.endpoint && (
          <p className="text-sm text-muted-foreground truncate" title={provider.endpoint}>
            {provider.endpoint}
          </p>
        )}

        {/* Validation error */}
        {provider.validation_error && (
          <div className="mt-2 p-2 rounded-md bg-red-50 dark:bg-red-950/20 text-red-700 dark:text-red-300 text-sm">
            <XCircle className="inline w-4 h-4 mr-1" />
            {provider.validation_error}
          </div>
        )}

        {/* Capabilities */}
        <div className="mt-2 flex flex-wrap gap-1">
          {provider.supports_streaming && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
              {t('admin.llmConfig.capabilities.streaming')}
            </span>
          )}
          {provider.supports_tools && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
              {t('admin.llmConfig.capabilities.tools')}
            </span>
          )}
          {provider.supports_vision && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
              {t('admin.llmConfig.capabilities.vision')}
            </span>
          )}
        </div>

        {/* Last validated */}
        {provider.last_validated_at && (
          <p className="mt-2 text-xs text-muted-foreground">
            {t('admin.llmConfig.lastValidated', { time: formatRelativeTime(provider.last_validated_at) ?? '' })}
          </p>
        )}
      </CardContent>

      <CardFooter className="gap-2 pt-0">
        {/* Validate button */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => onValidate(provider.id)}
          disabled={isValidating}
        >
          {isValidating ? (
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4 mr-1" />
          )}
          {t('common.validate')}
        </Button>

        {/* Edit button */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => onEdit(provider)}
        >
          <Edit className="w-4 h-4 mr-1" />
          {t('common.edit')}
        </Button>

        {/* Delete button */}
        {showDeleteConfirm ? (
          <div className="flex gap-1">
            <Button
              variant="destructive"
              size="sm"
              onClick={() => {
                onDelete(provider.id);
                setShowDeleteConfirm(false);
              }}
            >
              {t('common.confirm')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDeleteConfirm(false)}
            >
              {t('common.cancel')}
            </Button>
          </div>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20"
            onClick={() => setShowDeleteConfirm(true)}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}
